import { api } from './api';

export interface DocumentComment {
  id: string;
  documentId: string;
  userId: string;
  userName: string; // Nome do usuário vindo do backend
  comment: string;
  parentCommentId?: string;
  updated: boolean;
  updatedAt: string;
  createdAt?: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  children?: DocumentComment[];
}

export interface CreateDocumentCommentRequest {
  documentId: string;
  comment: string;
  parentCommentId?: string;
}

export interface UpdateDocumentCommentRequest {
  id: string;
  comment: string;
}

class DocumentCommentService {
  /**
   * Busca todos os comentários de um documento
   */
  async getDocumentComments(documentId: string): Promise<DocumentComment[]> {
    try {
      const response = await api.get(`/documents/comments?documentId=${documentId}`);
      return response.data;
    } catch (error: any) {
      console.error('[documentCommentService.getDocumentComments] Erro na requisição:', error);
      console.error('[documentCommentService.getDocumentComments] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  /**
   * Busca um comentário específico por ID
   */
  async getCommentById(id: string): Promise<DocumentComment> {
    try {
      const response = await api.get(`/documents/comments/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('[documentCommentService.getCommentById] Erro na requisição:', error);
      console.error('[documentCommentService.getCommentById] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  /**
   * Cria um novo comentário
   */
  async createComment(data: CreateDocumentCommentRequest): Promise<DocumentComment> {
    try {
      const response = await api.post('/documents/comments', data);
      return response.data;
    } catch (error: any) {
      console.error('[documentCommentService.createComment] Erro na requisição:', error);
      console.error('[documentCommentService.createComment] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  /**
   * Atualiza um comentário existente
   */
  async updateComment(data: UpdateDocumentCommentRequest): Promise<DocumentComment> {
    try {
      const response = await api.put('/documents/comments', data);
      return response.data;
    } catch (error: any) {
      console.error('[documentCommentService.updateComment] Erro na requisição:', error);
      console.error('[documentCommentService.updateComment] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  /**
   * Remove um comentário
   */
  async deleteComment(id: string): Promise<void> {
    try {
      await api.delete(`/documents/comments/${id}`);
    } catch (error: any) {
      console.error('[documentCommentService.deleteComment] Erro na requisição:', error);
      console.error('[documentCommentService.deleteComment] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  /**
   * Remove múltiplos comentários
   */
  async deleteManyComments(ids: string[]): Promise<void> {
    try {
      await api.delete('/documents/comments/batch', { data: ids });
    } catch (error: any) {
      console.error('[documentCommentService.deleteManyComments] Erro na requisição:', error);
      console.error('[documentCommentService.deleteManyComments] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }
}

export const documentCommentService = new DocumentCommentService();
